// Runtime theming.
// The app hardcodes its teal brand color (#3A8C8C / rgb 58 140 140, plus the
// darker hover shade #2E7A7A) in ~600 places. Rather than refactor all of them,
// we scan every loaded stylesheet and build a single override stylesheet that
// swaps the teal family for the admin-chosen primary color. Every Tailwind
// arbitrary value (bg-[#3A8C8C], its /opacity tints, hover:, etc.) compiles to a
// rule containing that RGB, so one pass recolors the entire app.

const TEAL = { r: 58, g: 140, b: 140 };        // #3A8C8C  — primary
const TEAL_HOVER = { r: 46, g: 122, b: 122 };  // #2E7A7A  — darker hover
const OVERRIDE_ID = 'is-theme-override';

export const PRESET_PALETTES = [
  { id: 'teal', name: 'Teal', primary: '#3A8C8C' },
  { id: 'indigo', name: 'Indigo', primary: '#4F5BD5' },
  { id: 'ocean', name: 'Ocean', primary: '#2C7DA0' },
  { id: 'forest', name: 'Forest', primary: '#3B7A57' },
  { id: 'plum', name: 'Plum', primary: '#8B5C9E' },
  { id: 'rose', name: 'Rose', primary: '#B5556E' },
  { id: 'terracotta', name: 'Terracotta', primary: '#C2683F' },
  { id: 'slate', name: 'Slate', primary: '#4A6785' },
];

export function hexToRgb(hex) {
  const h = (hex || '').replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
}
function toHex({ r, g, b }) {
  return '#' + [r, g, b].map(x => Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0')).join('');
}
function darken({ r, g, b }, amt = 0.14) {
  return { r: Math.round(r * (1 - amt)), g: Math.round(g * (1 - amt)), b: Math.round(b * (1 - amt)) };
}
// A very light tint of the color (mixed with white) — used for the app background.
function lightTint({ r, g, b }, k = 0.12) {
  return {
    r: Math.round(255 * (1 - k) + r * k),
    g: Math.round(255 * (1 - k) + g * k),
    b: Math.round(255 * (1 - k) + b * k),
  };
}
function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s; const l = (max + min) / 2;
  if (max === min) { h = 0; s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Returns a function that swaps teal-family color tokens in a CSS value string.
function makeSwapper(primary) {
  const pairs = [[TEAL, primary], [TEAL_HOVER, darken(primary, 0.14)]];
  return (val) => {
    let out = val;
    for (const [from, to] of pairs) {
      out = out
        .split(`${from.r} ${from.g} ${from.b}`).join(`${to.r} ${to.g} ${to.b}`)
        .split(`${from.r}, ${from.g}, ${from.b}`).join(`${to.r}, ${to.g}, ${to.b}`);
      const fromHex = toHex(from);
      if (out.toLowerCase().includes(fromHex)) out = out.replace(new RegExp(fromHex, 'ig'), toHex(to));
    }
    return out;
  };
}

function collectRules(rules, swap, out) {
  for (const rule of Array.from(rules)) {
    if (rule.type === 1 && rule.style && rule.selectorText) { // STYLE_RULE
      const decls = [];
      for (let i = 0; i < rule.style.length; i++) {
        const prop = rule.style[i];
        const val = rule.style.getPropertyValue(prop);
        const nv = swap(val);
        if (nv !== val) decls.push(`${prop}:${nv} !important;`);
      }
      if (decls.length) out.push(`${rule.selectorText}{${decls.join('')}}`);
    } else if (rule.type === 4 && rule.cssRules) { // MEDIA_RULE
      const inner = [];
      collectRules(rule.cssRules, swap, inner);
      if (inner.length) out.push(`@media ${rule.media.mediaText}{${inner.join('')}}`);
    }
  }
}

let lastTheme = null;

export function applyTheme(theme) {
  const primary = hexToRgb(theme?.primary) || TEAL;
  const key = toHex(primary);
  const swap = makeSwapper(primary);

  const out = [];
  for (const sheet of Array.from(document.styleSheets)) {
    if (sheet.ownerNode && sheet.ownerNode.id === OVERRIDE_ID) continue;
    let rules;
    try { rules = sheet.cssRules; } catch { continue; } // skip cross-origin sheets
    if (rules) collectRules(rules, swap, out);
  }

  let styleEl = document.getElementById(OVERRIDE_ID);
  if (!styleEl) { styleEl = document.createElement('style'); styleEl.id = OVERRIDE_ID; document.head.appendChild(styleEl); }
  styleEl.textContent = out.join('\n');

  // shadcn/Radix components read these CSS variables.
  const hsl = rgbToHsl(primary);
  const root = document.documentElement;
  ['--primary', '--accent', '--ring', '--chart-1'].forEach(v => root.style.setProperty(v, hsl));

  // App background = a light tint of the primary (follows the theme).
  const tint = lightTint(primary, 0.12);
  root.style.setProperty('--app-bg-rgb', `${tint.r} ${tint.g} ${tint.b}`);

  lastTheme = theme || { primary: key };
  setupThemeWatcher();
}

// Re-apply the last theme (used after new stylesheets load, e.g. HMR).
export function reapplyTheme() {
  if (lastTheme) applyTheme(lastTheme);
}

// Self-healing: CRA injects/replaces <style> tags asynchronously and on every
// hot-reload, so a single applyTheme() run can miss rules (this is why theming
// "worked sometimes"). Re-apply whenever stylesheets change or the page loads.
let _watcherReady = false;
function setupThemeWatcher() {
  if (_watcherReady || typeof window === 'undefined') return;
  _watcherReady = true;
  let t;
  const reapply = () => { clearTimeout(t); t = setTimeout(() => { if (lastTheme) applyTheme(lastTheme); }, 120); };
  try {
    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (n.nodeType === 1 && (n.tagName === 'STYLE' || n.tagName === 'LINK') && n.id !== OVERRIDE_ID) { reapply(); return; }
        }
      }
    });
    obs.observe(document.head, { childList: true });
  } catch { /* noop */ }
  window.addEventListener('load', reapply);
}

const CACHE_KEY = 'is_theme';
export function cachedTheme() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || 'null'); } catch { return null; }
}
export function cacheTheme(theme) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(theme)); } catch { /* noop */ }
}
